import { useState, useEffect } from 'react'
import {
  Alert,
  Box,
  Button,
  Flex,
  Input,
  Text,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@chakra-ui/react'
import { Info } from 'lucide-react'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import { useCreateBudget, useFiscalYears, useCurrentFiscalYear, useBudgets } from '../hooks'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type { BudgetType, BudgetCallFrequency } from '../types'
import { BUDGET_CALL_FREQUENCY_LABELS, BUDGET_TYPE_LABELS } from '../types'

interface BudgetCreateModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
}

export function BudgetCreateModal({
  isOpen,
  onClose,
  residenceId,
}: BudgetCreateModalProps) {
  const { data: fiscalYears } = useFiscalYears(residenceId)
  const { data: currentFiscalYear } = useCurrentFiscalYear(residenceId)
  const { data: budgets } = useBudgets(residenceId)
  const createBudget = useCreateBudget()

  const [formData, setFormData] = useState({
    name: '',
    budget_type: 'operating' as BudgetType,
    call_frequency: 'quarterly' as BudgetCallFrequency,
    fiscal_year_id: '',
    description: '',
  })

  // Calculer le montant total des budgets charges courantes pour l'exercice sélectionné
  const getOperatingBudgetTotal = () => {
    if (!budgets || !formData.fiscal_year_id) return 0
    return budgets
      .filter(b => b.budget_type === 'operating' && b.fiscal_year_id === formData.fiscal_year_id)
      .reduce((sum, b) => sum + (b.total_amount || 0), 0)
  }

  const operatingTotal = getOperatingBudgetTotal()
  const minimumALUR = operatingTotal * 0.05

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value)
  }

  // Initialiser l'exercice par défaut quand les données sont chargées
  useEffect(() => {
    if (currentFiscalYear && !formData.fiscal_year_id) {
      setFormData((prev) => ({ ...prev, fiscal_year_id: currentFiscalYear.id }))
    }
  }, [currentFiscalYear, formData.fiscal_year_id])

  const handleSubmit = async () => {
    if (!formData.name || !formData.fiscal_year_id) {
      toaster.create({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs obligatoires',
        type: 'error',
      })
      return
    }

    try {
      await createBudget.mutateAsync({
        residence_id: residenceId,
        name: formData.name,
        budget_type: formData.budget_type,
        call_frequency: formData.call_frequency,
        fiscal_year_id: formData.fiscal_year_id,
        description: formData.description || null,
        status: 'draft',
        total_amount: 0,
      })

      toaster.create({
        title: 'Succès',
        description: 'Budget créé avec succès',
        type: 'success',
      })
      handleClose()
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de la création',
        type: 'error',
      })
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      budget_type: 'operating',
      call_frequency: 'quarterly',
      fiscal_year_id: currentFiscalYear?.id || '',
      description: '',
    })
    onClose()
  }

  const getBudgetTypeLabel = (type: BudgetType) => {
    return BUDGET_TYPE_LABELS[type]
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="md">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Créer un budget</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Flex direction="column" gap={4}>
              <Field label="Nom du budget" required>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Budget prévisionnel 2025"
                />
              </Field>

              <Field label="Type de budget" required>
                <NativeSelectRoot>
                  <NativeSelectField
                    value={formData.budget_type}
                    onChange={(e) =>
                      setFormData({ ...formData, budget_type: e.target.value as BudgetType })
                    }
                  >
                    <option value="operating">{getBudgetTypeLabel('operating')}</option>
                    <option value="works">{getBudgetTypeLabel('works')}</option>
                    <option value="reserve">{getBudgetTypeLabel('reserve')}</option>
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>

              {formData.budget_type === 'reserve' && (
                <Alert.Root status="info" size="sm">
                  <Alert.Indicator>
                    <Info size={16} />
                  </Alert.Indicator>
                  <Alert.Content>
                    <Alert.Description fontSize="sm">
                      {operatingTotal > 0 ? (
                        <>
                          Le fonds travaux ALUR doit représenter au minimum 5% du budget charges courantes.
                          Montant minimum requis : <strong>{formatCurrency(minimumALUR)}</strong>
                        </>
                      ) : (
                        <>
                          Créez d'abord un budget charges courantes pour cet exercice afin de calculer
                          le montant minimum du fonds travaux ALUR (5%).
                        </>
                      )}
                    </Alert.Description>
                  </Alert.Content>
                </Alert.Root>
              )}

              <Field label="Périodicité des appels de fonds" required>
                <NativeSelectRoot>
                  <NativeSelectField
                    value={formData.call_frequency}
                    onChange={(e) =>
                      setFormData({ ...formData, call_frequency: e.target.value as BudgetCallFrequency })
                    }
                  >
                    {(Object.entries(BUDGET_CALL_FREQUENCY_LABELS) as [BudgetCallFrequency, string][]).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      )
                    )}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>

              <Field label="Exercice comptable" required>
                <NativeSelectRoot>
                  <NativeSelectField
                    value={formData.fiscal_year_id}
                    onChange={(e) =>
                      setFormData({ ...formData, fiscal_year_id: e.target.value })
                    }
                  >
                    <option value="">Sélectionner un exercice</option>
                    {fiscalYears?.map((fy) => (
                      <option key={fy.id} value={fy.id}>
                        {fy.name}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>

              <Field label="Description">
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description optionnelle"
                />
              </Field>

              <Box p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="sm" color="blue.700">
                  Le budget permettra de définir les charges prévisionnelles pour l'exercice
                  et servira de base pour les appels de fonds.
                </Text>
              </Box>
            </Flex>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={handleClose}>
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={createBudget.isPending}
            >
              Créer le budget
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
